import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";

interface LogoProps {
  variant?: "mark" | "full";
  size?: "sm" | "md" | "lg" | "xl";
  tone?: "light" | "dark";
  className?: string;
  to?: string;
  ariaLabel?: string;
}

const markSize: Record<NonNullable<LogoProps["size"]>, string> = {
  sm: "h-7 w-7",
  md: "h-9 w-9",
  lg: "h-11 w-11",
  xl: "h-14 w-14",
};

const textSize: Record<NonNullable<LogoProps["size"]>, string> = {
  sm: "text-sm",
  md: "text-base",
  lg: "text-lg",
  xl: "text-2xl",
};

export function Logo({
  variant = "full",
  size = "md",
  tone = "dark",
  className,
  to,
  ariaLabel,
}: LogoProps) {
  const content = (
    <>
      <LogoMark className={markSize[size]} />
      {variant === "full" && (
        <span
          className={cn(
            "font-bold tracking-tight",
            textSize[size],
            tone === "light" ? "text-white" : "text-ink-900"
          )}
        >
          Ustorio
        </span>
      )}
    </>
  );

  if (to) {
    return (
      <Link
        to={to}
        aria-label={ariaLabel ?? "Ana sayfa"}
        className={cn(
          "inline-flex items-center gap-2.5 rounded-md outline-none transition-opacity hover:opacity-90 focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2",
          className
        )}
      >
        {content}
      </Link>
    );
  }

  return (
    <div className={cn("flex items-center gap-2.5", className)}>{content}</div>
  );
}

function LogoMark({ className }: { className?: string }) {
  return (
    <svg
      viewBox="0 0 32 32"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      aria-hidden
    >
      <defs>
        <linearGradient id="ustorio-brand" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0" stopColor="#FBBF24" />
          <stop offset="0.55" stopColor="#F59E0B" />
          <stop offset="1" stopColor="#D97706" />
        </linearGradient>
        <linearGradient id="ustorio-gloss" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#ffffff" stopOpacity="0.35" />
          <stop offset="0.5" stopColor="#ffffff" stopOpacity="0" />
        </linearGradient>
      </defs>
      <rect width="32" height="32" rx="8" fill="url(#ustorio-brand)" />
      <rect width="32" height="32" rx="8" fill="url(#ustorio-gloss)" />
      {/* Wrench (lucide-react path), 24×24 grid centered at 4,4 */}
      <g transform="translate(4 4)">
        <path
          d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"
          stroke="white"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </g>
    </svg>
  );
}

export { LogoMark };
